import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui';
import FirmDocumentDialogSearchableSelect from 'pages/Documents/FirmDocuments/components/FirmDocumentDialogSearchableSelect';
import type { FirmDocumentDialogSearchableSelectOption } from 'pages/Documents/FirmDocuments/components/FirmDocumentDialogSearchableSelect';
import { shortDate } from 'utilities';
import type {
  FirmDocumentCopyDialogProps,
  FirmDocumentCopyTargetMeasurementDate,
} from './FirmDocumentCopyDialog.props';
import { firmDocumentDialogErrorAlertSx } from './FirmDocumentFileActionDialogs.styles';
import { FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS } from './FirmDocumentFileActionDialogs.testIds';

const getMeasurementDateLabel = (measurementDate: Pick<FirmDocumentCopyTargetMeasurementDate, 'date' | 'name'>) =>
  shortDate(measurementDate.date) ?? measurementDate.name;

const FirmDocumentCopyDialog = ({
  documentCount = 1,
  errorMessage = null,
  initialTargetMeasurementDateId = null,
  isCopying = false,
  isMeasurementDatesLoading = false,
  measurementDates,
  onClose,
  onConfirm,
  onErrorClear,
  open,
  sourceMeasurementDateId,
}: FirmDocumentCopyDialogProps) => {
  const targetMeasurementDates = useMemo(
    () => measurementDates.filter((measurementDate): measurementDate is FirmDocumentCopyTargetMeasurementDate => (
      typeof measurementDate.id === 'number' && measurementDate.id !== sourceMeasurementDateId
    )),
    [measurementDates, sourceMeasurementDateId]
  );
  const [targetMeasurementDateId, setTargetMeasurementDateId] = useState<number | null>(null);
  const selectedMeasurementDate = useMemo(
    () => targetMeasurementDates.find(measurementDate => measurementDate.id === targetMeasurementDateId),
    [targetMeasurementDateId, targetMeasurementDates]
  );
  const measurementDateOptions = useMemo<FirmDocumentDialogSearchableSelectOption[]>(
    () => targetMeasurementDates.map(measurementDate => ({
      id: measurementDate.id,
      label: getMeasurementDateLabel(measurementDate),
      searchText: `${measurementDate.name} ${measurementDate.date}`,
    })),
    [targetMeasurementDates]
  );
  const canCopy = Boolean(selectedMeasurementDate) && !isCopying;

  const handleConfirm = () => {
    if (selectedMeasurementDate) {
      onConfirm(selectedMeasurementDate);
    }
  };

  const handleTargetChange = (measurementDateId: number | null) => {
    setTargetMeasurementDateId(measurementDateId);
    onErrorClear?.();
  };

  // Reset transient target state when a fresh copy dialog opens, not during its close animation.
  useEffect(() => {
    if (open) {
      setTargetMeasurementDateId(null);
    }
  }, [open]);

  // Preselect the intended measurement-date target when a cross-MD drag opens this dialog.
  useEffect(() => {
    if (open && initialTargetMeasurementDateId) {
      setTargetMeasurementDateId(initialTargetMeasurementDateId);
    }
  }, [initialTargetMeasurementDateId, open]);

  // Clear stale target IDs when refreshed measurement-date options no longer include them.
  useEffect(() => {
    if (targetMeasurementDateId && !selectedMeasurementDate) {
      setTargetMeasurementDateId(null);
    }
  }, [selectedMeasurementDate, targetMeasurementDateId]);

  return (
    <Dialog
      data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.copyDialog}
      maxWidth="xs"
      onClose={isCopying ? undefined : onClose}
      open={open}>
      <DialogHeader>
        <DialogTitle>
          Copy to Another Measurement Date
        </DialogTitle>
        <DialogCloseButton
          aria-label="Close copy document dialog"
          data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.copyCloseButton}
          disabled={isCopying}
          onClick={onClose}
          size="small">
          <DialogCloseIcon />
        </DialogCloseButton>
      </DialogHeader>
      <DialogContent>
        <DialogDescription>
          {documentCount === 1
            ? 'Choose the measurement date that should receive this document copy.'
            : `Choose the measurement date that should receive these ${documentCount} document copies.`}
        </DialogDescription>
        <FirmDocumentDialogSearchableSelect
          clearSearchAriaLabel="Clear measurement date search"
          disabled={isCopying || isMeasurementDatesLoading || targetMeasurementDates.length === 0}
          emptyLabel="No measurement dates found"
          isLoading={isMeasurementDatesLoading}
          itemLabelPlural="measurement dates"
          itemLabelSingular="measurement date"
          label="Measurement Date"
          menuTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.copyTargetMenu}
          onChange={handleTargetChange}
          options={measurementDateOptions}
          placeholder={targetMeasurementDates.length > 0
            ? 'Select Measurement Date'
            : 'No measurement dates available'}
          searchPlaceholder="Find a Measurement Date"
          searchTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.copyTargetSearch}
          selectTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.copyTargetSelect}
          selectedId={targetMeasurementDateId}
          testIdForOption={id => FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.copyTargetOption(id ?? 0)}
        />
        {errorMessage ? (
          <Alert
            data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.copyErrorMessage}
            severity="error"
            sx={firmDocumentDialogErrorAlertSx}
            visualVariant="inline">
            {errorMessage}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActionFooter
        actionButtonTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.copyConfirmButton}
        actionLabel={documentCount === 1 ? 'Copy Document' : 'Copy Documents'}
        cancelButtonTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.copyCancelButton}
        isActionDisabled={!canCopy}
        isActionLoading={isCopying}
        isCancelDisabled={isCopying}
        onAction={handleConfirm}
        onCancel={onClose}
      />
    </Dialog>
  );
};

export default FirmDocumentCopyDialog;
